import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Fulfillment } from 'api/models/Fulfillment';
import { ASC, AttioQueryKeys, DESC } from 'common/constants/analyst-dashboards';
import {
  FULFILLMENT_COMPANY_NAME_KEY,
  FULFILLMENT_DATE_KEY,
  FULFILLMENT_MODEL_DUE_DATE_KEY,
  FULFILLMENT_MODEL_STATUS_KEY,
} from 'dashboard409a/common/constants/analyst-dashboard';
import { loadBulkConsultants, loadBulkValuations } from 'dashboard409a/services/attio-dashboard';
import { getAttioTeamMemberDetail, getAttioValuationByRecordId } from 'dashboard409a/services/attio-operation';
import {
  ModelWithValuationData,
  UseModelsWithValuationDataProps,
  UseModelsWithValuationDataReturn,
} from 'dashboard409a/services/models.types';
import { Logger } from 'utilities';
import { useModelsList } from '../services/models';

// Constants
const DEFAULT_ORDER_BY = FULFILLMENT_DATE_KEY;
const DEFAULT_ORDER = DESC;
const MODELS_SORT_KEY = 'modelsSort';
const BULK_VALUATION_BATCH_SIZE = 250;

export const useModelsWithValuationData = ({
  filterType,
  preparerUserIds,
  reviewerUserIds,
  orderBy = FULFILLMENT_DATE_KEY,
  order = DESC,
}: UseModelsWithValuationDataProps): UseModelsWithValuationDataReturn => {
  // Determine the actual sort order to use (from props, localStorage, or defaults)
  const actualSortOrder = useMemo(() => {
    const localSort = localStorage.getItem(MODELS_SORT_KEY);

    // If orderBy/order are different from defaults, use them
    if (orderBy !== DEFAULT_ORDER_BY || order !== DEFAULT_ORDER) {
      return { sortBy: orderBy, sortOrder: order };
    }

    // Otherwise, check localStorage
    if (localSort) {
      try {
        const parsed = JSON.parse(localSort);
        return { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder };
      } catch {
        // If parsing fails, use defaults
        return { sortBy: orderBy, sortOrder: order };
      }
    }

    // Use defaults
    return { sortBy: orderBy, sortOrder: order };
  }, [orderBy, order]);

  // Fetch models data using the existing API
  const { data: modelsData, isLoading: isLoadingModels, error: modelsError } = useModelsList(
    filterType,
    preparerUserIds,
    reviewerUserIds
  );

  const models = useMemo(() => {
    const response = modelsData as { results?: ModelWithValuationData[] } | undefined;
    return response?.results || [];
  }, [modelsData]);
  const queryClient = useQueryClient();

  // State for valuation data loading
  const [displayedModels, setDisplayedModels] = useState<ModelWithValuationData[] | null>(null);
  const [valuationDataLoaded, setValuationDataLoaded] = useState(false);
  const bulkAbortControllerRef = useRef<AbortController | null>(null);

  // Helper function to get valuation lead data using React Query for caching
  const getValuationLeadData = useCallback(
    async (valuationLeadRecordId: string): Promise<{ name: string; imageSrc: string }> => {
      // Check React Query cache first
      const cachedData = queryClient.getQueryData<{ name: string; imageSrc: string }>([
        AttioQueryKeys.VALUATION_LEAD,
        valuationLeadRecordId,
      ]);

      if (cachedData) {
        return cachedData;
      }

      try {
        const valuationLeadData = await getAttioTeamMemberDetail(valuationLeadRecordId);
        const leadData = {
          name: valuationLeadData.name || '-',
          imageSrc: valuationLeadData.imageSrc || '-',
        };

        // Cache in React Query
        queryClient.setQueryData([AttioQueryKeys.VALUATION_LEAD, valuationLeadRecordId], leadData);

        return leadData;
      } catch {
        // If error getting lead details, return default and cache it
        const defaultData = { name: '-', imageSrc: '-' };
        queryClient.setQueryData([AttioQueryKeys.VALUATION_LEAD, valuationLeadRecordId], defaultData);
        return defaultData;
      }
    },
    [queryClient]
  );

  // Memoize the models transformation to avoid unnecessary recalculations
  const modelsWithValuationData = useMemo(() => {
    if (!models || models.length === 0) {
      return null;
    }

    // Convert models to ModelWithValuationData format
    // Check React Query cache for existing valuation data
    return models.map((model: ModelWithValuationData) => {
      // Try to get cached valuation date
      const cachedValuationDate = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.VALUATION_DATE, model.attio_valuation_id])
        : undefined;

      // Try to get cached engagement type
      const cachedEngagementType = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.ENGAGEMENT_TYPE, model.attio_valuation_id])
        : undefined;

      // Try to get cached valuation lead record ID first
      const cachedValuationLeadRecordId = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.VALUATION_LEAD_RECORD_ID, model.attio_valuation_id])
        : undefined;

      // If we have the lead record ID, try to get the cached lead data
      let cachedValuationLead: string | undefined;
      let cachedValuationLeadImageSrc: string | undefined;
      if (cachedValuationLeadRecordId) {
        const cachedLeadData = queryClient.getQueryData<{ name: string; imageSrc: string }>([
          AttioQueryKeys.VALUATION_LEAD,
          cachedValuationLeadRecordId,
        ]);
        if (cachedLeadData) {
          cachedValuationLead = cachedLeadData.name;
          cachedValuationLeadImageSrc = cachedLeadData.imageSrc;
        }
      }

      const hasCachedData = cachedValuationDate !== undefined;

      return {
        ...model,
        is_loading_valuation_data: !hasCachedData,
        valuation_date: cachedValuationDate || undefined,
        engagement_type: cachedEngagementType || undefined,
        valuation_lead: cachedValuationLead || undefined,
        valuation_lead_image_src: cachedValuationLeadImageSrc || undefined,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  // Load initial data when models are loaded
  useEffect(() => {
    // Only update displayedModels when we have actual data (not during loading transitions)
    if (modelsWithValuationData) {
      setDisplayedModels(modelsWithValuationData);
    } else if (modelsData && !isLoadingModels) {
      // Only set empty array if we're not loading and have confirmed empty data
      // This prevents clearing displayedModels during loading transitions
      setDisplayedModels([]);
    }
    // If modelsData is null/undefined and we're loading, preserve existing displayedModels
  }, [modelsWithValuationData, modelsData, isLoadingModels]);

  // Reset valuationDataLoaded when models change
  useEffect(() => {
    setValuationDataLoaded(false);
    // Cleanup: abort any ongoing requests when models change
    if (bulkAbortControllerRef.current) {
      bulkAbortControllerRef.current.abort();
      bulkAbortControllerRef.current = null;
    }
  }, [models]);

  // Bulk load all valuation data for all models
  const loadAllValuationData = useCallback(
    async () => {
      if (!displayedModels || displayedModels.length === 0 || valuationDataLoaded) {
        return;
      }

      // Abort any existing request before starting a new one
      if (bulkAbortControllerRef.current) {
        bulkAbortControllerRef.current.abort();
      }

      // Create new AbortController for this bulk load
      const abortController = new AbortController();
      bulkAbortControllerRef.current = abortController;
      const { signal } = abortController;
      
      let isMounted = true;

      try {
        // Collect all unique valuation IDs from ALL models
        const valuationIds = [...new Set(
          displayedModels
            .filter(model => model.attio_valuation_id)
            .map(model => model.attio_valuation_id)
        )];

        if (valuationIds.length === 0) {
          setValuationDataLoaded(true);
          return;
        }

        // Batch the valuation IDs into smaller chunks
        const batches: string[][] = [];
        for (let i = 0; i < valuationIds.length; i += BULK_VALUATION_BATCH_SIZE) {
          batches.push(valuationIds.slice(i, i + BULK_VALUATION_BATCH_SIZE));
        }

        // Process batches sequentially to avoid overwhelming the server/browser
        const allValuationDataMap = new Map<string, {
          valuationDate: string;
          engagementType: string;
          valuationLeadRecordId?: string;
        }>();
        const allValuationLeadRecordIds: string[] = [];

        // Process batches sequentially using reduce to avoid linter issues with for...of + await
        await batches.reduce(async (previousPromise, batch) => {
          await previousPromise;
          
          // Check if aborted before processing each batch
          if (signal.aborted) {
            return;
          }

          // Load valuations for this batch
          const valuationsMap = await loadBulkValuations(batch, signal);

          // Check if aborted after loading batch
          if (signal.aborted) {
            return;
          }

          // Process results from this batch
          valuationsMap.forEach((valuation, valuationId) => {
            // Extract valuation_date - bulk endpoint returns as string
            let valuationDate: string;
            if (typeof valuation.valuation_date === 'string') {
              valuationDate = valuation.valuation_date;
            } else if (Array.isArray(valuation.valuation_date) && valuation.valuation_date[0]?.value) {
              valuationDate = valuation.valuation_date[0].value;
            } else {
              valuationDate = '-';
            }
            
            // Extract engagement_type - bulk endpoint returns as simple string
            let engagementType: string;
            if (typeof valuation.engagement_type === 'string' && valuation.engagement_type) {
              engagementType = valuation.engagement_type;
            } else if (Array.isArray(valuation.engagement_type) && valuation.engagement_type[0]?.option?.title) {
              engagementType = valuation.engagement_type[0].option.title;
            } else {
              engagementType = '-';
            }
            
            // Extract valuation_lead_94 - bulk endpoint returns as simple string (record ID)
            let valuationLeadRecordId: string | undefined;
            if (typeof valuation.valuation_lead_94 === 'string' && valuation.valuation_lead_94) {
              valuationLeadRecordId = valuation.valuation_lead_94;
            } else if (Array.isArray(valuation.valuation_lead_94) && valuation.valuation_lead_94[0]?.target_record_id) {
              valuationLeadRecordId = valuation.valuation_lead_94[0].target_record_id;
            }

            allValuationDataMap.set(valuationId, {
              valuationDate,
              engagementType,
              valuationLeadRecordId,
            });

            // Cache valuation date
            queryClient.setQueryData([AttioQueryKeys.VALUATION_DATE, valuationId], valuationDate);

            // Cache engagement type
            queryClient.setQueryData([AttioQueryKeys.ENGAGEMENT_TYPE, valuationId], engagementType);

            // Cache the relationship between valuation ID and lead record ID
            if (valuationLeadRecordId) {
              queryClient.setQueryData(
                [AttioQueryKeys.VALUATION_LEAD_RECORD_ID, valuationId],
                valuationLeadRecordId
              );
              allValuationLeadRecordIds.push(valuationLeadRecordId);
            }
          });
        }, Promise.resolve());

        // Check if aborted before loading leads
        if (signal.aborted) {
          return;
        }

        // Bulk load all valuation leads
        const uniqueLeadIds = [...new Set(allValuationLeadRecordIds)];
        const valuationLeadsMap = uniqueLeadIds.length > 0
          ? await loadBulkConsultants(uniqueLeadIds, signal)
          : new Map<string, { name: string; imageSrc: string }>();

        // Check if aborted before updating state
        if (signal.aborted) {
          return;
        }

        // Cache all valuation leads in React Query
        valuationLeadsMap.forEach((data, id) => {
          queryClient.setQueryData([AttioQueryKeys.VALUATION_LEAD, id], data);
        });

        // Update all models with the loaded data (only if still mounted)
        if (isMounted && !signal.aborted) {
          setDisplayedModels(prev => {
            if (!prev) {
              return prev;
            }

            return prev.map(model => {
              if (!model.attio_valuation_id) {
                return {
                  ...model,
                  is_loading_valuation_data: false,
                };
              }

              const valuationData = allValuationDataMap.get(model.attio_valuation_id);
              if (!valuationData) {
                return {
                  ...model,
                  valuation_date: '-',
                  engagement_type: '-',
                  valuation_lead: '-',
                  valuation_lead_image_src: '-',
                  is_loading_valuation_data: false,
                };
              }

              const leadData = valuationData.valuationLeadRecordId
                ? valuationLeadsMap.get(valuationData.valuationLeadRecordId) || { name: '-', imageSrc: '-' }
                : { name: '-', imageSrc: '-' };

              return {
                ...model,
                valuation_date: valuationData.valuationDate,
                engagement_type: valuationData.engagementType,
                valuation_lead: leadData.name,
                valuation_lead_image_src: leadData.imageSrc,
                is_loading_valuation_data: false,
              };
            });
          });

          setValuationDataLoaded(true);
        }
      } catch (error) {
        // Don't log AbortError - it's expected when cancelled
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        Logger.error('Error loading valuation data', error);
      } finally {
        isMounted = false;
        // Only clear loading state if not aborted and still mounted
        if (!signal.aborted && isMounted) {
          setValuationDataLoaded(true);
        }
        // Clear the ref if this was the current controller
        if (bulkAbortControllerRef.current === abortController) {
          bulkAbortControllerRef.current = null;
        }
      }
    },
    [displayedModels, valuationDataLoaded, queryClient]
  );

  // Function to load valuation data for a single model (kept in case we need it for the individual model loading)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadValuationDataForModel = useCallback(
    async (model: ModelWithValuationData) => {
      if (!model.attio_valuation_id) {
        return;
      }

      // Check if we already have cached data for this valuation
      const cachedValuationDate = queryClient.getQueryData<string>([
        AttioQueryKeys.VALUATION_DATE,
        model.attio_valuation_id,
      ]);

      const cachedEngagementType = queryClient.getQueryData<string>([
        AttioQueryKeys.ENGAGEMENT_TYPE,
        model.attio_valuation_id,
      ]);

      if (cachedValuationDate !== undefined) {
        // We have cached date, check if we have cached lead record ID
        const cachedValuationLeadRecordId = queryClient.getQueryData<string>([
          AttioQueryKeys.VALUATION_LEAD_RECORD_ID,
          model.attio_valuation_id,
        ]);

        if (cachedValuationLeadRecordId) {
          const cachedLead = queryClient.getQueryData<{ name: string; imageSrc: string }>([
            AttioQueryKeys.VALUATION_LEAD,
            cachedValuationLeadRecordId,
          ]);

          if (cachedLead) {
            // Update model with cached data
            setDisplayedModels(
              prev =>
                prev?.map(m =>
                  m.id === model.id
                    ? {
                      ...m,
                      valuation_date: cachedValuationDate,
                      valuation_lead: cachedLead.name,
                      valuation_lead_image_src: cachedLead.imageSrc,
                      is_loading_valuation_data: false,
                      engagement_type: cachedEngagementType,
                    }
                    : m
                ) || []
            );
            return;
          }
        }

        // If we don't have cached lead record ID, fetch it
        const valuationResponse = await getAttioValuationByRecordId(model.attio_valuation_id).catch(() => null);
        const valuationLeadRecordId = valuationResponse?.data?.values?.valuation_lead_94?.[0]?.target_record_id;

        if (valuationLeadRecordId) {
          // Cache the relationship between valuation ID and lead record ID
          queryClient.setQueryData(
            [AttioQueryKeys.VALUATION_LEAD_RECORD_ID, model.attio_valuation_id],
            valuationLeadRecordId
          );

          const cachedLead = queryClient.getQueryData<{ name: string; imageSrc: string }>([
            AttioQueryKeys.VALUATION_LEAD,
            valuationLeadRecordId,
          ]);

          if (cachedLead) {
            // Update model with cached data
            setDisplayedModels(
              prev =>
                prev?.map(m =>
                  m.id === model.id
                    ? {
                      ...m,
                      valuation_date: cachedValuationDate,
                      valuation_lead: cachedLead.name,
                      valuation_lead_image_src: cachedLead.imageSrc,
                      is_loading_valuation_data: false,
                    }
                    : m
                ) || []
            );
            return;
          }
        }
      }

      try {
        // Get valuation details
        const valuationResponse = await getAttioValuationByRecordId(model.attio_valuation_id);
        const valuationDate = valuationResponse.data.values.valuation_date?.[0]?.value || '-';
        const valuationLeadRecordId = valuationResponse.data.values.valuation_lead_94?.[0]?.target_record_id;
        const engagementType = valuationResponse.data.values.engagement_type?.[0]?.option.title || '-';

        // Cache valuation date in React Query
        queryClient.setQueryData([AttioQueryKeys.VALUATION_DATE, model.attio_valuation_id], valuationDate);
        queryClient.setQueryData([AttioQueryKeys.ENGAGEMENT_TYPE, model.attio_valuation_id], engagementType);
        // Cache the relationship between valuation ID and lead record ID
        if (valuationLeadRecordId) {
          queryClient.setQueryData(
            [AttioQueryKeys.VALUATION_LEAD_RECORD_ID, model.attio_valuation_id],
            valuationLeadRecordId
          );
        }

        // Get valuation lead details if available (using cache)
        let valuationLead = '-';
        let valuationLeadImageSrc = '';
        if (valuationLeadRecordId) {
          const leadData = await getValuationLeadData(valuationLeadRecordId);
          valuationLead = leadData.name;
          valuationLeadImageSrc = leadData.imageSrc;
        }

        // Update the model with valuation data
        setDisplayedModels(
          prev =>
            prev?.map(m =>
              m.id === model.id
                ? {
                  ...m,
                  valuation_date: valuationDate,
                  valuation_lead: valuationLead,
                  valuation_lead_image_src: valuationLeadImageSrc,
                  is_loading_valuation_data: false,
                  engagement_type: engagementType,
                }
                : m
            ) || []
        );
      } catch {
        // Error loading valuation data for model
        // Cache error state
        const errorDate = '-';
        queryClient.setQueryData([AttioQueryKeys.VALUATION_DATE, model.attio_valuation_id], errorDate);

        // Update with error state
        setDisplayedModels(
          prev =>
            prev?.map(m =>
              m.id === model.id
                ? { ...m, valuation_date: errorDate, valuation_lead: '-', is_loading_valuation_data: false }
                : m
            ) || []
        );
      }
    },
    [queryClient, getValuationLeadData]
  );

  // Load valuation data for all models using bulk loading
  useEffect(() => {
    let isMounted = true;

    if (displayedModels && displayedModels.length > 0 && !valuationDataLoaded) {
      // Check if any models need valuation data
      const needsValuationData = displayedModels.some(
        model => model.attio_valuation_id && model.is_loading_valuation_data
      );

      if (needsValuationData) {
        loadAllValuationData();
      } else if (isMounted) {
        setValuationDataLoaded(true);
      }
    }

    // Cleanup: abort any ongoing requests when component unmounts or dependencies change
    return () => {
      isMounted = false;
      if (bulkAbortControllerRef.current) {
        bulkAbortControllerRef.current.abort();
        bulkAbortControllerRef.current = null;
      }
    };
  }, [displayedModels, valuationDataLoaded, loadAllValuationData]);

  // Function to update a model's note
  const updateModelNote = useCallback((modelId: number, noteContent: string, noteId?: number) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      return prev.map(model => {
        if (model.id === modelId) {
          return {
            ...model,
            note: {
              id: noteId ?? model.note?.id ?? 0, // Use provided noteId, or keep existing ID, or use 0 for new notes
              content: noteContent,
              author_id: model.note?.author_id || 0,
              author_email: model.note?.author_email || '',
              author_name: model.note?.author_name || '',
              created_at: model.note?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          };
        }
        return model;
      });
    });
  }, []);

  // Function to update a model's status
  const updateModelStatus = useCallback((modelId: number, newStatus: number) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }

      return prev.map(model => {
        if (model.id === modelId) {
          return {
            ...model,
            model_status: newStatus as Fulfillment.model_status,
          };
        }
        return model;
      });
    });
  }, []);

  // Function to update a model's supporting_due_date
  const updateModelDueDate = useCallback((modelId: number, newDueDate: string) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      return prev.map(model => {
        if (model.id === modelId) {
          return {
            ...model,
            supporting_due_date: newDueDate,
          };
        }
        return model;
      });
    });
  }, []);

  // Sort the displayed models based on orderBy and order
  const sortedModels = useMemo(() => {
    if (!displayedModels) {
      return [];
    }

    const { sortBy, sortOrder } = actualSortOrder;

    // Save to localStorage
    localStorage.setItem(MODELS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));

    return [...displayedModels].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortBy) {
        case FULFILLMENT_COMPANY_NAME_KEY:
          aValue = a.company_name || '';
          bValue = b.company_name || '';
          break;
        case FULFILLMENT_MODEL_STATUS_KEY:
          aValue = a.model_status || '';
          bValue = b.model_status || '';
          break;
        case FULFILLMENT_MODEL_DUE_DATE_KEY:
          aValue = ((a as unknown as Record<string, unknown>).model_due_date as string) || '';
          bValue = ((b as unknown as Record<string, unknown>).model_due_date as string) || '';
          break;
        case FULFILLMENT_DATE_KEY:
          aValue = a.valuation_date || '';
          bValue = b.valuation_date || '';
          break;
        default:
          aValue = ((a as unknown as Record<string, unknown>)[sortBy] as string) || '';
          bValue = ((b as unknown as Record<string, unknown>)[sortBy] as string) || '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === ASC ? comparison : -comparison;
      }

      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const comparison = aValue - bValue;
        return sortOrder === ASC ? comparison : -comparison;
      }

      // Fallback to string comparison
      const comparison = String(aValue).localeCompare(String(bValue));
      return sortOrder === ASC ? comparison : -comparison;
    });
  }, [displayedModels, actualSortOrder]);

  // Determine if we should show loading
  // Show loading if:
  // 1. Models are still loading, OR
  // 2. displayedModels is null (initial state)
  const shouldShowLoading = isLoadingModels || displayedModels === null;

  return {
    rows: sortedModels,
    totalCount: sortedModels.length,
    isLoading: shouldShowLoading,
    isError: !!modelsError,
    error: modelsError,
    updateModelNote,
    updateModelStatus,
    updateModelDueDate,
  };
};
